import { ArrowUp, CircleStop, Loader2, Mic, Paperclip, Square } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';
import { useToastStore } from '@shared/stores/toast.store';

import { tokenCount } from '../data/context-figures';
import { levelName } from '../data/level-text';
import type { ModelName, ModelOption } from '../data/models';
import { levelAfterPick, type Level, type ThinkingChoice } from '../data/thinking-levels';
import type { PickerPolicy } from '../data-policy/picker-policy';
import { useModelControls } from '../hooks/use-model-controls';
import { useVoiceInput } from '../hooks/use-voice-input';
import type { AttachedFile, CompactionOutcome } from '../types';

import { AttachmentChip } from './AttachmentChip';
import { ComposerNote, type ComposerNoteKind } from './ComposerNote';
import { CHIP_LABEL_MIN_WIDTH, ModelPicker } from './ModelPicker';
import { ThinkingPicker } from './ThinkingPicker';
import { VoiceWaveform } from './VoiceWaveform';
import { ComposerBoxContext } from './composer-box';

/**
 * Only what the orchestrator's upload route accepts (iq-ai-cio
 * `file_manager._ALLOWED_CONTENT_TYPES`): it refuses `.doc`, `.json` and `.xml`
 * with a 400, so offering them only produced a chip whose file never reached the
 * chat. iq-ai-cio's `tests/api/test_files_upload_accept.py` vendors this list and
 * posts each extension to the real route, so change both together.
 */
const ACCEPTED_FILE_TYPES = '.pdf,.docx,.xls,.xlsx,.pptx,.csv,.txt,.png,.jpg,.jpeg';

/** Per the AI CIO Restrictions spec: max 10 attachments, 512 MB combined per request. */
const MAX_ATTACHMENTS = 10;
const MAX_TOTAL_ATTACHMENT_BYTES = 512 * 1024 * 1024;

interface ComposerProps {
  modelId: string;
  /** The chat's session may run a model set elsewhere (see shownModel). */
  sessionModelUnknown?: boolean;
  /** The chat's session is a connected session: it runs the deployment's model, nothing to pick. */
  connected?: boolean;
  /** The picked model's name rides with its id, so the chat can name it once the list omits it. */
  onModelChange: (modelId: string, name?: ModelName) => void;
  onSend: (text: string, files?: AttachedFile[]) => void;
  /** Extra classes for the outer card (e.g. max-width on the full page). */
  className?: string;
  autoFocusInput?: boolean;
  /** Locks the whole input (typing, attach, voice, model, send) — e.g. a view-only Vault file. */
  disabled?: boolean;
  /** A reply is being written: Enter queues the message, and Stop replaces Send. */
  busy?: boolean;
  /** Stop was pressed and the reply has not ended. */
  stopping?: boolean;
  /**
   * A compaction holds the chat's session: typing, attach, a new recording and Send wait (the
   * draft stays), the placeholder says so, and Send shows, never Stop; the chips stay usable, and
   * a recording already running can be stopped.
   */
  compacting?: boolean;
  /** Stops the reply being written: the Stop button, and Esc. */
  onStop?: () => void;
  /** Up on an empty composer: the queued messages come back to edit, as one text. */
  onTakeBack?: () => Promise<string>;
  /** The chat's level (Conversation.thinking, else the user's last choice); null: none chosen. */
  thinking?: ThinkingChoice | null;
  /** Sets the chat's level. Absent: no Thinking chip; a pick writes no level and shows no note. */
  onThinkingChange?: (choice: ThinkingChoice) => void;
  /** The chat's Agentic switch; absent reads as off. */
  agentic?: boolean;
  /** Sets the chat's switch. Absent: the Thinking popover has no Agentic row. */
  onAgenticChange?: (on: boolean) => void;
  /** The chat the composer writes to: the Lowered note goes when it moves to another chat. */
  conversationId?: string;
  /** The chat's model's label and provider as picked: names it while /models no longer lists it. */
  modelName?: ModelName;
  /**
   * The token meter (DEV-4598): the first of the footer's inline-end group, before the Model
   * chip. Absent where the composer has no session (a new chat, the Vault dock).
   */
  meter?: ReactNode;
  /** How this chat's last compaction press ended; its note shows while no compaction runs. */
  compactionNote?: CompactionOutcome | null;
  /** Takes that note off: its Dismiss, and a pick that lowers the level while it shows. */
  onCompactionNoteDismiss?: () => void;
  policy?: PickerPolicy;
  waitingForPolicy?: boolean;
}

/**
 * A model pick lowered the thinking level: the picked model's label, the level it landed on,
 * and what Undo puts back (the model and the level shown before the pick).
 */
interface LoweredNote {
  model: string;
  level: Level;
  undo: { modelId: string; name?: ModelName; level: ThinkingChoice };
}

/**
 * Shared chat input used by both the full-page chatbot and the side panel (DEV-4575 Option C).
 * A bordered rounded card: the textarea on top; attach and mic on the footer's inline start;
 * the token meter (DEV-4598, given as `meter`), the Model chip, the Thinking chip and Send on its
 * inline end; between them, one note: while a compaction runs, that it runs; else how this tab's
 * compaction press ended; else, while a model pick has lowered the thinking level, a note that
 * says so, with Undo, and while the chat's model is no longer offered, one that says so, with
 * Choose model; Send waits until a model is picked (the draft stays). The card measures its own
 * width, not the viewport's: below CHIP_LABEL_MIN_WIDTH (the side panel at its default width) the
 * Model chip shows its logo only and the Thinking chip's level name may truncate; the meter never
 * gives way. Every popover anchors to the card.
 */
export function Composer({
  modelId,
  sessionModelUnknown,
  connected,
  onModelChange,
  onSend,
  className,
  autoFocusInput,
  disabled = false,
  busy = false,
  stopping = false,
  compacting = false,
  onStop,
  onTakeBack,
  thinking,
  onThinkingChange,
  agentic,
  onAgenticChange,
  conversationId,
  modelName,
  meter,
  compactionNote = null,
  onCompactionNoteDismiss,
  policy,
  waitingForPolicy = false,
}: ComposerProps) {
  const { t, i18n } = useTranslation('ai-cio');
  const addToast = useToastStore((s) => s.addToast);
  const [value, setValue] = useState('');
  const [files, setFiles] = useState<AttachedFile[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const idSeq = useRef(0);
  /** The note row's live region, always mounted. */
  const noteRef = useRef<HTMLDivElement>(null);
  /** The card's width; 0 until it is measured, which reads as wide. */
  const [width, setWidth] = useState(0);
  /** The note shown is too long for one 24 px line (a narrow composer), so it wraps. */
  const [noteWraps, setNoteWraps] = useState(false);

  const measure = useCallback(() => {
    const el = rootRef.current;
    if (el) setWidth(el.getBoundingClientRect().width);
    const note = noteRef.current;
    if (note) setNoteWraps(note.offsetHeight > 24);
  }, []);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (noteRef.current) observer.observe(noteRef.current);
    return () => {
      observer.disconnect();
    };
  }, [measure]);

  const focusInput = useCallback(() => {
    textareaRef.current?.focus();
  }, []);
  /** What the meter needs of the composer: the box its popover anchors to, and the textarea. */
  const box = useMemo(() => ({ boxRef: rootRef, focusInput }), [focusInput]);

  const appendTranscript = useCallback((text: string) => {
    setValue((prev) => (prev ? `${prev} ${text}` : text));
    textareaRef.current?.focus();
  }, []);
  const voice = useVoiceInput(appendTranscript);

  const controls = useModelControls(
    modelId,
    { sessionModelUnknown, connected },
    onThinkingChange ? { choice: thinking ?? null } : undefined,
    modelName,
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const compact = width > 0 && width < CHIP_LABEL_MIN_WIDTH;
  /** The Lowered note: never persisted, no timer. */
  const [note, setNote] = useState<LoweredNote | null>(null);
  /** The model whose Unavailable note was dismissed: it stays hidden for that model. */
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const { unavailable } = controls;
  /** The Unavailable note shows while no Lowered note does. */
  const unavailableNote = !note && unavailable !== null && dismissedFor !== modelId;
  /**
   * The note row shows one note: while a compaction runs, that it runs (nothing clears it);
   * else how this tab's press ended; else the Lowered note, else the Unavailable one.
   */
  const shownNote = compacting
    ? 'compacting'
    : compactionNote
      ? 'compaction'
      : note
        ? 'lowered'
        : unavailableNote
          ? 'unavailable'
          : null;

  // Another chat opened in this composer drops the note. A first id is the side panel's first
  // pick creating the chat, which keeps the note it just set.
  const prevConversationId = useRef(conversationId);
  useEffect(() => {
    const prev = prevConversationId.current;
    prevConversationId.current = conversationId;
    if (prev !== undefined && prev !== conversationId) setNote(null);
  }, [conversationId]);

  // While the chat's model is unavailable, or a compaction holds the session, Send and Enter
  // wait, and the draft stays.
  const canSend =
    !disabled &&
    !compacting &&
    !waitingForPolicy &&
    unavailable === null &&
    (value.trim().length > 0 || files.length > 0);
  /** A reply is being written: Stop replaces Send. A compaction is not one, and has no Stop. */
  const stoppable = busy && !compacting && !waitingForPolicy && onStop !== undefined;

  /**
   * A model picked: kept on the chat, and the level it lands on stored in the chat, so the level
   * is never raised by a switch, and picking the first model again shows the lowered level.
   */
  function handlePick(option: ModelOption) {
    const { level, choice, models, shown } = controls;
    const landing = levelAfterPick(models?.thinking_scale, option.thinking, level, choice);
    // First: in the side panel with no active chat it creates the chat the level is written to.
    onModelChange(option.id, { label: option.label, groupId: option.group.id });
    if (landing && onThinkingChange) onThinkingChange({ id: landing.id, rank: landing.rank });
    // The name shown before the pick, for Undo: the listed model's, else the chat's stored one
    // (the unavailable model's, or none).
    const before: ModelName | undefined = shown
      ? { label: shown.label, groupId: shown.group.id }
      : modelName;
    // A pick that lowers the level says so, with Undo; any other pick drops an earlier note.
    const lowers = Boolean(onThinkingChange && level && landing && landing.rank < level.rank);
    setNote(
      lowers && level && landing
        ? {
            model: option.label,
            level: landing,
            undo: { modelId, name: before, level: { id: level.id, rank: level.rank } },
          }
        : null,
    );
    // Its note takes the row from a press's outcome; a compaction running keeps the row.
    if (lowers && shownNote === 'compaction') onCompactionNoteDismiss?.();
    // A pick ends the unavailable state: an Undo back to that model shows its note again.
    setDismissedFor(null);
  }

  /** Undo: the model and the level shown before the pick, in one step; Agentic stays. */
  function handleUndo() {
    if (!note) return;
    onModelChange(note.undo.modelId, note.undo.name);
    onThinkingChange?.(note.undo.level);
    setNote(null);
    textareaRef.current?.focus();
  }

  function handleDismiss() {
    if (shownNote === 'compaction') onCompactionNoteDismiss?.();
    else if (note) setNote(null);
    else setDismissedFor(modelId);
    textareaRef.current?.focus();
  }

  /** The note of how this tab's press ended: its icon and its sentence. */
  function compactionNoteOf(outcome: CompactionOutcome): { kind: ComposerNoteKind; text: string } {
    switch (outcome.kind) {
      case 'compacted':
        return {
          kind: 'compacted',
          text:
            outcome.before !== null && outcome.after !== null
              ? t(
                  'composer.noteCompacted',
                  'Conversation compacted: {{before}} → {{after}} tokens.',
                  {
                    before: tokenCount(outcome.before, i18n.language),
                    after: tokenCount(outcome.after, i18n.language),
                  },
                )
              : t('composer.noteCompactedPlain', 'Conversation compacted.'),
        };
      case 'nothing':
        return { kind: 'info', text: t('composer.noteCompactNothing', 'Nothing to compact yet.') };
      case 'internalOff':
        return {
          kind: 'info',
          text: t(
            'composer.noteCompactInternalOff',
            "Compaction on Investbanq's own model is not available yet.",
          ),
        };
      case 'declined':
        return {
          kind: 'alert',
          text: t('composer.noteCompactDeclined', 'This conversation could not be compacted.'),
        };
      case 'unavailable':
        return {
          kind: 'alert',
          text: t(
            'composer.noteCompactUnavailable',
            'Compaction is unavailable right now. Try again shortly.',
          ),
        };
      case 'failed':
        return { kind: 'alert', text: t('composer.noteCompactFailed', 'Compaction failed.') };
    }
  }

  function handleAttachClick() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const list = e.target.files;
    if (list && list.length > 0) {
      let bytes = files.reduce((sum, f) => sum + f.size, 0);
      let slotsLeft = MAX_ATTACHMENTS - files.length;
      const accepted: AttachedFile[] = [];
      let overCount = false;
      let overSize = false;

      for (const file of Array.from(list)) {
        if (slotsLeft <= 0) {
          overCount = true;
          continue;
        }
        if (bytes + file.size > MAX_TOTAL_ATTACHMENT_BYTES) {
          overSize = true;
          continue;
        }
        bytes += file.size;
        slotsLeft -= 1;
        accepted.push({
          id: `${Date.now().toString(36)}-${(idSeq.current++).toString(36)}`,
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          file,
        });
      }

      if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted]);

      if (overCount) {
        addToast({
          variant: 'error',
          title: t('composer.maxAttachments', 'Maximum {{count}} attachments allowed', {
            count: MAX_ATTACHMENTS,
          }),
        });
      }
      if (overSize) {
        addToast({
          variant: 'error',
          title: t(
            'composer.maxAttachmentSize',
            'File size exceeds limit (max 512MB). Please choose a smaller file.',
          ),
        });
      }
    }
    // Reset so the same file can be selected again.
    e.target.value = '';
  }

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function handleSend() {
    if (!canSend) return;
    const text = value.trim();
    onSend(text, files.length > 0 ? files : undefined);
    setValue('');
    setFiles([]);
    setNote(null);
    textareaRef.current?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      // While a reply is being written, this queues the message behind it.
      e.preventDefault();
      handleSend();
    } else if (e.key === 'Escape' && stoppable) {
      // Esc stops the reply here, and never reaches the app's Escape that closes the panel.
      e.preventDefault();
      e.stopPropagation();
      if (!stopping) onStop();
    } else if (e.key === 'ArrowUp' && !value && onTakeBack) {
      e.preventDefault();
      void onTakeBack().then((text) => {
        if (text) setValue((prev) => (prev ? `${text}\n\n${prev}` : text));
      });
    }
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        'overflow-hidden rounded-xl border bg-background shadow-[0_1px_2px_rgba(0,0,0,0.06)]',
        className,
      )}
    >
      {/* Hidden file input — triggered by the Paperclip button */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES}
        className="hidden"
        aria-hidden="true"
        onChange={handleFileChange}
      />
      <textarea
        ref={textareaRef}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- focusing the composer is the expected behavior on the chat surface
        autoFocus={autoFocusInput}
        value={value}
        disabled={disabled || compacting}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        placeholder={
          compacting
            ? t('composer.compacting', 'Compacting…')
            : waitingForPolicy
              ? t('composer.policyWaiting', 'Choose a data policy to continue…')
              : t('composer.placeholder', 'Type your message...')
        }
        aria-label={t('composer.label', 'Message AI CIO')}
        // Three 20 px lines between 12 px above and 10 px below: the drawn 82 px, its text 12 px
        // in from the box. A note takes 34 px of it (its 24 px row and 10 px below), and 8 px more
        // when it wraps to a second line, so the box keeps its height. A block, so no line box
        // adds room under it.
        className={cn(
          'block w-full resize-none bg-transparent px-3 pb-2.5 pt-3 text-sm text-foreground outline-none placeholder:text-muted-foreground',
          shownNote ? (noteWraps ? 'h-10' : 'h-12') : 'h-[82px]',
        )}
      />

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pb-1 pt-2">
          {files.map((file) => (
            <AttachmentChip
              key={file.id}
              name={file.name}
              size={file.size}
              removeLabel={t('composer.removeFile', 'Remove file')}
              onRemove={() => {
                removeFile(file.id);
              }}
            />
          ))}
        </div>
      )}

      <div ref={noteRef} role="status" aria-live="polite">
        {shownNote === 'compacting' ? (
          <ComposerNote
            kind="compacting"
            text={t('composer.noteCompacting', 'Compacting the conversation…')}
          />
        ) : shownNote === 'compaction' && compactionNote ? (
          <ComposerNote {...compactionNoteOf(compactionNote)} onDismiss={handleDismiss} />
        ) : note ? (
          <ComposerNote
            kind="lowered"
            text={t('composer.lowered', 'Thinking lowered to {{level}} for {{model}}.', {
              level: levelName(t, note.level.id, note.level.label),
              model: note.model,
            })}
            actionLabel={t('composer.undo', 'Undo')}
            onAction={handleUndo}
            onDismiss={handleDismiss}
          />
        ) : (
          unavailableNote && (
            <ComposerNote
              kind="unavailable"
              text={t('composer.unavailable', '{{model}} is unavailable right now.', {
                model: unavailable.label,
              })}
              actionLabel={t('composer.chooseModel', 'Choose model')}
              onAction={() => {
                setPickerOpen(true);
              }}
              onDismiss={handleDismiss}
            />
          )
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-3 pb-3">
        {/* Inline start: attachment and mic */}
        <div className="flex shrink-0 items-center">
          <button
            type="button"
            onClick={handleAttachClick}
            disabled={disabled || compacting}
            className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            title={t('composer.attach', 'Attach file')}
            aria-label={t('composer.attach', 'Attach file')}
          >
            <Paperclip className="size-4" />
          </button>
          <button
            type="button"
            onClick={voice.toggle}
            // A compaction holds the mic, but never a recording it found running: the mic is
            // the one way to stop it.
            disabled={
              disabled ||
              voice.status === 'transcribing' ||
              (compacting && voice.status !== 'recording')
            }
            title={t('composer.voice', 'Voice input')}
            aria-label={t('composer.voice', 'Voice input')}
            aria-pressed={voice.status === 'recording'}
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              voice.status === 'recording'
                ? 'text-destructive hover:bg-destructive/10'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {voice.status === 'transcribing' ? (
              <Loader2 className="size-4 animate-spin" />
            ) : voice.status === 'recording' ? (
              <Square className="size-3.5 fill-current" />
            ) : (
              <Mic className="size-4" />
            )}
          </button>
        </div>

        {/* Inline end: the token meter, the Model chip, the Thinking chip, and Send, or Stop
            while a reply is being written, in the same footprint, so the chips never move. 4 px
            apart, and 8 px before Send as drawn; below CHIP_LABEL_MIN_WIDTH, 4 px throughout */}
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1">
          {meter ? (
            <ComposerBoxContext.Provider value={box}>{meter}</ComposerBoxContext.Provider>
          ) : null}
          <ModelPicker
            controls={controls}
            disabled={disabled}
            compact={compact}
            boxRef={rootRef}
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            onPick={handlePick}
            policy={policy}
          />
          {onThinkingChange && (
            <ThinkingPicker
              controls={controls}
              onChoiceChange={onThinkingChange}
              agentic={agentic === true}
              onAgenticChange={onAgenticChange}
              connected={connected}
              disabled={disabled}
              compact={compact}
              boxRef={rootRef}
              onOpenChange={(open) => {
                // Closing the popover drops the note; a move inside it leaves it, so nothing
                // shifts under the open popover.
                if (!open) setNote(null);
              }}
              noteRef={noteRef}
            />
          )}
          {stoppable ? (
            // Stop asks the AI CIO to stop; the reply goes on to its stop card.
            <button
              type="button"
              onClick={onStop}
              disabled={stopping}
              aria-label={
                stopping ? t('composer.stopping', 'Stopping…') : t('composer.stop', 'Stop')
              }
              title={
                stopping
                  ? t('composer.stopping', 'Stopping…')
                  : `${t('composer.stop', 'Stop')} (Esc)`
              }
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm transition-opacity',
                !compact && 'ms-1',
                stopping && 'cursor-not-allowed opacity-50',
              )}
            >
              {stopping ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <CircleStop className="size-4" />
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              aria-label={t('composer.send', 'Send')}
              title={t('composer.send', 'Send')}
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm transition-opacity',
                !compact && 'ms-1',
                !canSend && 'cursor-not-allowed opacity-50',
              )}
            >
              <ArrowUp className="size-4" />
            </button>
          )}
        </div>
      </div>

      {voice.status === 'recording' ? (
        // Live audio track while recording (tap the mic again to stop).
        <div className="px-3 pb-2">
          <VoiceWaveform active getLevel={voice.getLevel} />
        </div>
      ) : voice.status === 'transcribing' || voice.error ? (
        <div
          className={cn(
            'px-4 pb-2 text-xs',
            voice.error ? 'text-destructive' : 'text-muted-foreground',
          )}
          role="status"
          aria-live="polite"
        >
          {voice.error ?? t('voice.transcribing', 'Transcribing…')}
        </div>
      ) : null}
    </div>
  );
}
